/**
 * FIFO idle/waiter coordination for WorkerPool.
 *
 * This helper stays generic over the worker shape so it can be unit-tested
 * without spawning child processes, while WorkerPool keeps ownership of process
 * lifecycle and typed pool errors.
 *
 * These three functions are the only mutations of the waiter queue, which is why
 * the optional {@link WorkerPoolSaturationTracker} is fed from here: park, hand-off
 * and drain are the complete set of transitions its accounting depends on.
 */

import type { WorkerPoolSaturationTracker } from './saturation.js';

export type WorkerPoolWaiter<Worker> = {
  resolve: (worker: Worker) => void;
  reject: (error: Error) => void;
  /**
   * Clock reading taken when this caller parked. Undefined when no tracker was
   * passed — deliberately not `0`, so a tracker that somehow sees an untracked
   * waiter records *nothing* rather than a `Date.now()`-sized wait, which would
   * silently poison the max and never look wrong on a dashboard.
   */
  enqueuedAt?: number;
};

export const acquireScheduledWorker = <Worker>({
  idle,
  waiters,
  canSpawn,
  spawn,
  saturation,
}: {
  idle: Worker[];
  waiters: WorkerPoolWaiter<Worker>[];
  canSpawn: () => boolean;
  spawn: () => Promise<Worker>;
  saturation?: WorkerPoolSaturationTracker;
}): Promise<Worker> => {
  const free = idle.pop();
  if (free) return Promise.resolve(free);
  if (canSpawn()) return spawn();
  // Parked: no idle child and the pool is at `size`. This branch *is* saturation.
  return new Promise<Worker>((resolve, reject) => {
    waiters.push({ resolve, reject, enqueuedAt: saturation?.enqueued(waiters.length + 1) });
  });
};

export const releaseScheduledWorker = <Worker>({
  worker,
  idle,
  waiters,
  saturation,
}: {
  worker: Worker;
  idle: Worker[];
  waiters: WorkerPoolWaiter<Worker>[];
  saturation?: WorkerPoolSaturationTracker;
}): void => {
  const waiter = waiters.shift();
  if (waiter) {
    saturation?.dequeued(waiter.enqueuedAt, waiters.length);
    waiter.resolve(worker);
  } else {
    idle.push(worker);
  }
};

export const rejectScheduledWaiters = <Worker>(
  waiters: WorkerPoolWaiter<Worker>[],
  error: Error,
  saturation?: WorkerPoolSaturationTracker
): void => {
  const drained = waiters.splice(0);
  for (const waiter of drained) {
    // A wait that ended in failure is still a wait; the queue is empty after the
    // splice, so this also closes the starved interval.
    saturation?.dequeued(waiter.enqueuedAt, 0);
    waiter.reject(error);
  }
};
